import { JiraIcon } from '@atlaskit/logo';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { messages } from '@databases/intl/messages';
import React, { type FunctionComponent } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

const BRAND_APPEARANCE_FIELDS = ['summary', 'status', 'priority', 'assignee'];

const jiraIconContainerStyles = xcss({
  position: 'relative',
  display: 'inline-flex',
});

const JIRA_BADGE_SIZE = 12;

const BadgeWithTooltip = styled.span`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${JIRA_BADGE_SIZE}px;
  height: ${JIRA_BADGE_SIZE}px;
  bottom: ${token('space.negative.050')};
  right: ${token('space.negative.025')};
  border: 0.5px solid ${token('elevation.surface.sunken')};
  border-radius: 2.75px;
  pointer-events: auto;

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    top: 100%;
    margin-top: ${token('space.100')};
    left: 50%;
    transform: translateX(-50%);
    padding: ${token('space.050')} ${token('space.100')};
    background: ${token('color.background.neutral.bold')};
    color: ${token('color.text.inverse')};
    font: ${token('font.body.small')};
    border-radius: 3px;
    white-space: nowrap;
    visibility: hidden;
  }

  &:hover::after {
    opacity: 1;
    visibility: visible;
  }
`;

export interface JiraFieldIconWrapperProps {
  children: React.ReactNode;
  jiraField?: string;
}

export const JiraFieldIconWrapper: FunctionComponent<JiraFieldIconWrapperProps> = ({
  children,
  jiraField,
}) => {
  const { formatMessage } = useIntl();
  const isBrandAppearance = !jiraField || BRAND_APPEARANCE_FIELDS.includes(jiraField);
  const appearance = isBrandAppearance ? 'brand' : 'neutral';
  const tooltipContent = isBrandAppearance
    ? formatMessage(messages['jira-badge.synced-to-jira'])
    : formatMessage(messages['jira-badge.editable-in-jira-only']);

  const showBadge = false;

  return (
    <Box xcss={jiraIconContainerStyles} testId="jira-field-icon-wrapper">
      {children}
      {showBadge && (
        <BadgeWithTooltip data-tooltip={tooltipContent} data-testid="jira-badge">
          <JiraIcon
            appearance={appearance}
            size="xsmall"
            label=""
            shouldUseNewLogoDesign
          />
        </BadgeWithTooltip>
      )}
    </Box>
  );
};
